import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import app from '../../config/app'
import withMemo from '../../decorators/WithMemo'
import { routeParamSlug } from '../../helpers/UrlHelpers'
import { actions as CompanyActions, selectors as CompanySelectors } from '../../pages/Company/redux'
import { selectors as AppSelectors } from '../../redux/app/redux'
import { selectors as AuthSelectors } from '../../redux/auth/redux'
import routes, { Router } from '../../routes'

import CompanyDetails from './index'


function CompanyDetailsWrapper(props) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { company } = props
  const currentRoute = useSelector(AppSelectors.route)
  const user = useSelector(AuthSelectors.user)
  const isActivePremium = useSelector(AuthSelectors.isActivePremium)

  const deleteCommentSelector = useSelector(CompanySelectors.deleteComment)
  const submitErrors = deleteCommentSelector?.errors
  const submitSuccess = deleteCommentSelector?.success
  const submitPending = deleteCommentSelector?.pending

  const handleGoBack = () => Router.back()

  const commentMadeByUser = company?.comments?.find((comment) => comment?.user?.id === user?.id)
  const handleResetPostCommentForm = () => {
    dispatch(CompanyActions.postCommentReset())
  }
  const handleDeleteComment = (comment) => {
    dispatch(CompanyActions.deleteComment({ comment }))
  }

  const isOwnCompany = company.id === user?.company?.id

  const data = {
    ...props,
    sharing: {
      title: t('companyDetails_sharing_title'),
      twitterMessage: t('companyDetails_sharing_twitterMessage', { name: company?.title }),
      mailSubject: t('companyDetails_sharing_mailSubject', { name: company?.title }),
      mailBody: (company?.description ? `${company?.description}\n\n:url` : t('companyDetails_sharing_mailBody', { name: company?.title })),
      url: `${app.APP_URL}${currentRoute.asPath}`,
    },
    company: {
      ...company,
      contacts: company.isActivePremium ? company?.contacts : [],
      modalTitle: t('companyDetails_postComment'),
      commentsLabel: t('companyDetails_comments'),
      anonymousLabel: t('companyDetails_anonymous'),
      publishComment: t('companyDetails_publishComment'),
      recommendationLabel: t('companyDetails_recommendCompany'),
      noCommentLabel: t('companyDetails_noComment'),
      backLabel: t('companyDetails_backLabel'),
      backClick: handleGoBack,
      handleResetPostCommentForm,
      isOwnCompany,
      commentMadeByUser,
      // Cannot comment if target company is not premium
      // Cannot comment if current user company is not premium
      // Cannot comment if current user company is currently viewed company
      canComment: isActivePremium && company.isActivePremium && !isOwnCompany,
      comments: company?.comments?.map((comment) => ({
        ...comment,
        profileLink: Router.getRouteUrl(routes.profile, { slug: comment.user.slug }),
        profileRoute: routes.profile.name,
        profileRouteParams: { slug: routeParamSlug(comment.user.slug) },
        handleDelete: () => handleDeleteComment(comment),
        canDelete: !(submitPending || submitSuccess || submitErrors),
      })) ?? [],
      companyLink: Router.getRouteUrl(routes.company, { slug: company?.slug }),
      companyRoute: routes.company.name,
      companyRouteParams: { slug: routeParamSlug(company?.slug) },
      certificationsLabel: t('companyDetails_certificationsLabel'),
      plainCertificationsLabel: t('companyDetails_plain_certificationsLabel'),
      referencesLabel: t('companyDetails_referencesLabel'),
      contactsLabel: t('companyDetails_contactsLabel'),
      contactMembersLabel: t('companyDetails_contactMembersLabel'),
      contactMembers: company?.contactMembers?.map((contactMember) => ({
        ...contactMember,
        seeProfileLabel: t('companyDetails_seeProfileLabel'),
        seeProfileLink: Router.getRouteUrl(routes.profile, { slug: contactMember.slug }),
        seeProfileRoute: routes.profile.name,
        seeProfileRouteParams: { slug: routeParamSlug(contactMember.slug) },
      })),
      filesLabel: t('companyDetails_filesLabel'),
      filesSeeLabel: t('companyDetails_filesSeeLabel'),
      phoneLabel: t('companyDetails_phoneLabel'),
      websiteLabel: t('companyDetails_websiteLabel'),
      socialIconsLabel: t('companyDetails_socialIconsLabel'),
      metrics: company?.metrics.map((metric) => ({
        ...metric,
        ...metric.label === 'workforce' && {
          value: t(`companyWorkforce_${metric.value}`),
        },
        label: t(`companyDetails_metric_${metric.label}`),
      })).filter((metric) => !/^companyDetails_metric_/.test(metric.label) && !/^companyWorkforce_/.test(metric.value)),
      addresses: company?.addresses.map((address) => ({
        ...address,
        title: address.isMain ? `${t('companyDetails_mainAddressLabel').trim()} ${address.city}` : address.name,
      })),
      offers: null,
    },
  }

  return <CompanyDetails {...data} />
}

CompanyDetailsWrapper.propTypes = CompanyDetails.propTypes

CompanyDetailsWrapper.defaultProps = CompanyDetails.defaultProps

export default withMemo()(CompanyDetailsWrapper)
